import produce, { Draft } from "immer";
import Debug from "debug";
import { v4 as uuidv4 } from "uuid";

import {
  ViewerStore,
  StreamState,
  Stream,
  ActionViewerChannel,
  ViewerError,
  ActionViewerError,
  ActionViewerStreamState,
  BroadcastSettings,
  ActionViewerBroadcastSettings,
  ActionViewerPlaybackUrl,
  ActionViewerStreamCount,
  PayloadViewerVideoReaction,
  ActionViewerVideoReaction,
  ActionViewerVerifyPasswordSuccess,
  AuthResponse,
  ActionViewerRefreshTokenSuccess,
  ActionViewerAuthSuccess,
  PayloadViewerVideoReactionFull,
} from "../types";
import { ACTIONS } from "../actions/constants";

const debug = Debug("broadcast:reducers:viewer");

const processChannel = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: Stream | null
): void => {
  debug("processChannel", payload);
  draft.channel = payload;
};

const processBroadcastSettings = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: BroadcastSettings
): void => {
  const { ingestEndpoint, streamKey } = payload;
  if (draft.channel !== null) {
    draft.channel.ingestEndpoint = ingestEndpoint;
    draft.channel.streamKey = streamKey;
  }
};

const processPlackbackUrl = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: string
): void => {
  if (draft.channel !== null) {
    draft.channel.playbackUrl = payload;
  }
};

const loading = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: boolean
): void => {
  debug("loading", payload);
  draft.loading = payload;
};

const addError = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: ViewerError
): void => {
  debug("addError", payload);
  const errorId = uuidv4();
  const sameErrors = draft.errors.filter(
    (error) => error.error === payload.error
  );
  const differentErrors = draft.errors.filter(
    (error) => error.error !== payload.error
  );
  debug("same errors", sameErrors);
  draft.errors = [...differentErrors, { uuid: errorId, error: payload.error }];
};

const removeError = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: ViewerError
): void => {
  debug("removeError", payload);
  const { uuid } = payload;
  draft.errors = draft.errors.filter((error) => error.uuid !== uuid);
};

const setStreamState = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: StreamState
): void => {
  debug("setStreamState", payload);
  draft.streamState = payload;
};

const setStreamViewers = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: number
): void => {
  debug("setStreamViewers", payload);
  draft.viewers = payload;
};

const addStreamReaction = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: PayloadViewerVideoReactionFull
): void => {
  debug("addStreamReaction", payload);
  const result = draft.reactions.filter(
    (reaction) =>
      reaction.userName === payload.userName &&
      reaction.startTime === payload.startTime &&
      reaction.endTime === payload.endTime
  );
  debug("addStreamReaction existing", result);
  if (result.length === 0) {
    draft.reactions.push(payload);
  } else {
    debug("same reaction");
  }
};

const removeStreamReaction = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: PayloadViewerVideoReaction
): void => {
  debug("removeStreamReaction", payload);
  draft.reactions = draft.reactions.filter(
    (reaction) =>
      reaction.timestamp.getTime() !== payload.timestamp.getTime() &&
      reaction.userName !== payload.userName &&
      reaction.reaction !== payload.reaction
  );
};

const addTokens = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: AuthResponse
): void => {
  debug("addTokens", payload);
  const { accessToken, refreshToken, expiresAt } = payload;
  draft.token = accessToken;
  draft.refresh = {
    token: refreshToken,
    expires: expiresAt,
  };
};

const removeTokens = (viewer: ViewerStore, draft: Draft<ViewerStore>): void => {
  debug("removeTokens");
  draft.token = null;
  draft.refresh = {
    token: null,
    expires: null,
  };
};

const addUser = (
  viewer: ViewerStore,
  draft: Draft<ViewerStore>,
  payload: AuthResponse
): void => {
  debug("addUser", payload);
  const { user } = payload;

  if (user) {
    draft.user = user;
  }
};

const removeUser = (viewer: ViewerStore, draft: Draft<ViewerStore>): void => {
  debug("removeUser");

  draft.user = null;
};

export const viewerReducer = (
  viewer = {
    channel: null,
    errors: [],
    reactions: [],
    viewers: 0,
    loading: false,
    streamState: StreamState.Unknown,
    token: null,
    refresh: {
      token: null,
      expires: null,
    },
    user: null,
  },
  {
    type,
    payload,
  }:
    | ActionViewerChannel
    | ActionViewerError
    | ActionViewerStreamState
    | ActionViewerBroadcastSettings
    | ActionViewerPlaybackUrl
    | ActionViewerStreamCount
    | ActionViewerVideoReaction
    | ActionViewerVerifyPasswordSuccess
    | ActionViewerRefreshTokenSuccess
    | ActionViewerAuthSuccess
): ViewerStore =>
  produce(viewer, (draft) => {
    switch (type) {
      case ACTIONS.VIEWER_GET_CHANNEL_REQUEST: {
        loading(viewer, draft, true);
        break;
      }
      case ACTIONS.VIEWER_GET_CHANNEL_FAILURE: {
        loading(viewer, draft, false);
        addError(viewer, draft, payload as ViewerError);
        break;
      }
      case ACTIONS.VIEWER_GET_CHANNEL_SUCCESS: {
        loading(viewer, draft, false);
        processChannel(viewer, draft, payload as Stream);
        break;
      }

      case ACTIONS.VIEWER_STREAM_STATE_SUCCESS:
      case ACTIONS.VIEWER_STREAM_STATE_SET: {
        setStreamState(viewer, draft, payload as StreamState);
        break;
      }
      case ACTIONS.VIEWER_STREAM_STATE_FAILURE: {
        setStreamState(viewer, draft, StreamState.Offline);
        addError(viewer, draft, payload as ViewerError);
        break;
      }
      case ACTIONS.VIEWER_GET_BROADCAST_SETTINGS_SUCCESS: {
        processBroadcastSettings(viewer, draft, payload as BroadcastSettings);
        break;
      }
      case ACTIONS.VIEWER_GET_BROADCAST_SETTINGS_FAILURE: {
        loading(viewer, draft, false);
        addError(viewer, draft, payload as ViewerError);
        break;
      }
      case ACTIONS.VIEWER_GET_PLAYBACK_URL_SUCCESS: {
        processPlackbackUrl(viewer, draft, payload as string);
        break;
      }
      case ACTIONS.VIEWER_GET_PLAYBACK_URL_FAILURE: {
        loading(viewer, draft, false);
        addError(viewer, draft, payload as ViewerError);
        break;
      }
      case ACTIONS.VIEWER_COUNT_SUCCESS: {
        setStreamViewers(viewer, draft, payload as number);
        break;
      }
      case ACTIONS.VIEWER_COUNT_FAILURE: {
        addError(viewer, draft, payload as ViewerError);
        break;
      }
      case ACTIONS.VIEWER_REACT_TO_STREAM_FAILURE: {
        addError(viewer, draft, payload as ViewerError);
        break;
      }
      case ACTIONS.VIEWER_REACT_TO_STREAM_REACTION: {
        addStreamReaction(
          viewer,
          draft,
          payload as PayloadViewerVideoReactionFull
        );
        break;
      }
      case ACTIONS.VIEWER_REMOVE_STREAM_REACTION: {
        removeStreamReaction(
          viewer,
          draft,
          payload as PayloadViewerVideoReaction
        );
        break;
      }
      case ACTIONS.VIEWER_VERIFY_PASSWORD_REQUEST: {
        loading(viewer, draft, true);
        break;
      }
      case ACTIONS.VIEWER_VERIFY_PASSWORD_SUCCESS:
      case ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_SUCCESS: {
        loading(viewer, draft, false);
        addTokens(viewer, draft, payload as AuthResponse);
        break;
      }
      case ACTIONS.VIEWER_VERIFY_PASSWORD_FAILURE:
      case ACTIONS.VIEWER_AUTH_FAILURE: {
        loading(viewer, draft, false);
        addError(viewer, draft, payload as ViewerError);
        break;
      }
      case ACTIONS.VIEWER_REFRESH_TOKEN_POLLING_FAILURE: {
        addError(viewer, draft, payload as ViewerError);
        break;
      }
      case ACTIONS.VIEWER_AUTH_REQUEST: {
        loading(viewer, draft, true);
        break;
      }
      case ACTIONS.VIEWER_AUTH_SUCCESS: {
        loading(viewer, draft, false);
        addTokens(viewer, draft, payload as AuthResponse);
        addUser(viewer, draft, payload as AuthResponse);
        break;
      }
      case ACTIONS.VIEWER_AUTH_REMOVE: {
        removeTokens(viewer, draft);
        removeUser(viewer, draft);
        break;
      }
      case ACTIONS.EDITOR_REMOVE_ERROR_CONFIRM: {
        removeError(viewer, draft, payload as ViewerError);
        break;
      }
      default:
        break;
    }
  });
